import React, { useState, useEffect } from 'react';
import { updateEmail } from 'firebase/auth';
import { doc, updateDoc, setDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { User, Mail, Phone, MapPin, Home, Settings, Save, X, Package } from 'lucide-react';
import './ProfileManagement.css';

function ProfileManagement({ user, isAdmin = false }) {
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US'
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [orders, setOrders] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [originalData, setOriginalData] = useState({});

  useEffect(() => {
    loadProfile();
    if (!isAdmin) {
      loadUserOrders();
    }
  }, [user, isAdmin]);

  const loadProfile = async () => {
    if (!user) return;

    try {
      setProfileData(prev => ({
        ...prev,
        name: user.displayName || '',
        email: user.email || ''
      }));

      // Try to load additional profile data from Firestore
      const profileQuery = query(
        collection(db, 'profiles'),
        where('userId', '==', user.uid)
      );
      
      const profileSnapshot = await getDocs(profileQuery);
      if (!profileSnapshot.empty) {
        const profileDoc = profileSnapshot.docs[0];
        const profileInfo = profileDoc.data();
        setProfileData(prev => ({
          ...prev,
          ...profileInfo
        }));
        setOriginalData(profileInfo);
      }
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  const loadUserOrders = async () => {
    if (!user) return;

    try {
      const ordersQuery = query(
        collection(db, 'orders'),
        where('customer.email', '==', user.email),
        orderBy('createdAt', 'desc')
      );
      
      const ordersSnapshot = await getDocs(ordersQuery);
      const ordersData = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setOrders(ordersData);
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  };

  const handleChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value
    });
  };

  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  const countries = [
    { code: 'US', name: 'United States' },
    { code: 'TT', name: 'Trinidad and Tobago' },
    { code: 'CA', name: 'Canada' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'JM', name: 'Jamaica' },
    { code: 'BB', name: 'Barbados' },
    { code: 'GY', name: 'Guyana' },
    { code: 'GD', name: 'Grenada' },
    { code: 'LC', name: 'St. Lucia' },
    { code: 'VC', name: 'St. Vincent & Grenadines' },
    { code: 'DM', name: 'Dominica' },
    { code: 'AG', name: 'Antigua & Barbuda' },
    { code: 'BS', name: 'Bahamas' },
    { code: 'BZ', name: 'Belize' },
    { code: 'CR', name: 'Costa Rica' },
    { code: 'PA', name: 'Panama' },
    { code: 'MX', name: 'Mexico' }
  ];

  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      // Update email if changed
      if (profileData.email !== user.email) {
        await updateEmail(user, profileData.email);
      }

      // Save complete profile data to Firestore
      const profileRef = doc(db, 'profiles', user.uid);
      const profileDataToSave = {
        userId: user.uid,
        name: profileData.name,
        email: profileData.email,
        phone: profileData.phone,
        address: profileData.address,
        address2: profileData.address2,
        city: profileData.city,
        state: profileData.state,
        zipCode: profileData.zipCode,
        country: profileData.country,
        updatedAt: new Date()
      };

      await setDoc(profileRef, profileDataToSave, { merge: true });
      
      // Update original data and set editing state to false
      setOriginalData(profileDataToSave);
      setIsEditing(false);
      
      showMessage('Account information saved successfully!', 'success');

    } catch (error) {
      console.error('Error updating profile:', error);
      showMessage(error.message || 'Failed to update profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = () => {
    setIsEditing(true);
    setMessage('');
  };

  const cancelEditing = () => {
    setProfileData(prev => ({
      ...prev,
      ...originalData
    }));
    setIsEditing(false);
    setMessage('');
  };

  const hasChanges = () => {
    return JSON.stringify(profileData) !== JSON.stringify({ ...profileData, ...originalData });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate();
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="profile-management">
      <div className="profile-header">
        <div className="profile-title">
          <h1>{isAdmin ? 'Admin Profile' : 'My Profile'}</h1>
          <p>Manage your account settings and information</p>
        </div>
        <div className="profile-avatar">
          <User size={48} />
        </div>
      </div>

      {message && (
        <div className={`message ${messageType}`}>
          {message}
        </div>
      )}

      <div className="profile-content">
        <div className="profile-form-section">
          <h2><Settings size={20} /> Account Information</h2>
          <form onSubmit={saveProfile} className="profile-form">
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="name">
                  <User size={16} />
                  Full Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={profileData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  disabled={!isEditing}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">
                  <Mail size={16} />
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={profileData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  disabled={!isEditing}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">
                  <Phone size={16} />
                  Phone Number
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={profileData.phone}
                  onChange={handleChange}
                  placeholder="(868) 123-4567"
                  disabled={!isEditing}
                />
              </div>

              <div className="form-group">
                <label htmlFor="country">Country</label>
                <select
                  id="country"
                  name="country"
                  value={profileData.country}
                  onChange={handleChange}
                  disabled={!isEditing}
                >
                  {countries.map(country => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="address">
                  <MapPin size={16} />
                  Street Address
                </label>
                <input
                  id="address"
                  name="address"
                  type="text"
                  value={profileData.address}
                  onChange={handleChange}
                  placeholder="123 Main Street"
                  disabled={!isEditing}
                />
              </div>

              <div className="form-group">
                <label htmlFor="address2">Address Line 2</label>
                <input
                  id="address2"
                  name="address2"
                  type="text"
                  value={profileData.address2}
                  onChange={handleChange}
                  placeholder="Apt 4B, Suite 200"
                  disabled={!isEditing}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="city">
                    <Home size={16} />
                    City
                  </label>
                  <input
                    id="city"
                    name="city"
                    type="text"
                    value={profileData.city}
                    onChange={handleChange}
                    placeholder="Port of Spain"
                    disabled={!isEditing}
                  />
                </div>

                {profileData.country === 'US' ? (
                  <div className="form-group">
                    <label htmlFor="state">State</label>
                    <select
                      id="state"
                      name="state"
                      value={profileData.state}
                      onChange={handleChange}
                      disabled={!isEditing}
                    >
                      <option value="">Select State</option>
                      {states.map(state => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="form-group">
                    <label htmlFor="state">State/Province</label>
                    <input
                      id="state"
                      name="state"
                      type="text"
                      value={profileData.state}
                      onChange={handleChange}
                      placeholder="St. George"
                      disabled={!isEditing}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="zipCode">
                    {profileData.country === 'US' ? 'ZIP Code' : 'Postal Code'}
                  </label>
                  <input
                    id="zipCode"
                    name="zipCode"
                    type="text"
                    value={profileData.zipCode}
                    onChange={handleChange}
                    placeholder={profileData.country === 'US' ? '10001' : 'ABC123'}
                    disabled={!isEditing}
                  />
                </div>
              </div>
            </div>

            <div className="form-actions">
            {!isEditing ? (
              <button 
                type="button" 
                className="edit-button" 
                onClick={startEditing}
              >
                <Settings size={20} />
                Edit Profile
              </button>
            ) : (
              <>
                <button 
                  type="button" 
                  className="cancel-button" 
                  onClick={cancelEditing}
                  disabled={loading}
                >
                  <X size={20} />
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="save-button" 
                  disabled={loading || !hasChanges()}
                >
                  <Save size={20} />
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            )}
          </div>
          </form>
        </div>

        {!isAdmin && orders.length > 0 && (
          <div className="recent-orders">
            <h2><Package size={20} /> Recent Orders</h2>
            <div className="orders-list">
              {orders.slice(0, 5).map(order => (
                <div key={order.id} className="order-item">
                  <div className="order-info">
                    <div className="order-id">#{order.id.slice(-6).toUpperCase()}</div>
                    <div className="order-date">{formatDate(order.createdAt)}</div>
                  </div>
                  <div className="order-total">${order.total?.toFixed(2)}</div>
                  <div 
                    className="order-status"
                    style={{ backgroundColor: getStatusColor(order.status) }}
                  >
                    {order.status}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function getStatusColor(status) {
  const colors = {
    pending: '#f59e0b',
    processing: '#3b82f6',
    shipped: '#8b5cf6',
    delivered: '#10b981',
    cancelled: '#ef4444'
  };
  return colors[status] || '#6b7280';
}

export default ProfileManagement;
